import React, { useEffect, useState } from 'react';

// WU-F (RJ, staging pass 2026-08-09): "show the user something that assures
// them the product is actually working on verification when it is
// processing."
//
// Between "Submit evidence for checking" and a result there was nothing —
// no motion, no clock, no sign a machine had the file. A check takes tens
// of seconds, and silence for tens of seconds reads as a hang.
//
// Stage words are the customer's, never Plan/Collect/Judge (CLAUDE.md,
// Customer Vocabulary). Past the stall threshold the widget says so rather
// than animating forever, which is the honest version of a wait
// (PRD-031 F2.3).

const STAGE_WORDS = {
  planning: 'Working out what to check',
  collecting: 'Reading the files',
  judging: 'Checking against the outcome',
};

// Ten minutes, matching VerificationActions and the server-side run-health
// alert. At 90 seconds this surface declared a stall the other two surfaces
// disagreed with, and a normal run is still reading files at that point.
const STALL_AFTER_MS = 10 * 60 * 1000;

function elapsedLabel(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export function stageWordFor(status) {
  return STAGE_WORDS[status] || 'Checking';
}

// The render decision, as a value the tests can hold.
//
// Same precedent as WU-C's evidenceCtaFor: a widget that appeared on a
// failed or finished run would assure the user of work that is not
// happening, which is worse than the silence it replaced — so the guard is
// a predicate, not a condition buried in JSX where only a rendering test
// could reach it (QA round 2, F6).
export function shouldShowCheck(run) {
  return Boolean(run && STAGE_WORDS[run.status]);
}

// Stalled is measured from the last SIGN OF LIFE, not from the start — the
// same basis the per-Outcome line uses. Measuring from the start let this
// widget call a healthy long run slow while the line directly below it
// reported fresh progress (QA round 2, F5).
export function isStalled(run, now = Date.now()) {
  if (!run) return false;
  const lastSign = run.last_event_at || run.started_at || run.created_at;
  if (!lastSign) return false;
  const at = new Date(lastSign).getTime();
  if (Number.isNaN(at)) return false;
  return now - at > STALL_AFTER_MS;
}

export default function CheckInProgress({ run, outcomeName, onRetry, retrying = false, retryError = '' }) {
  // The clock ticks locally: the run list polls every few seconds, and a
  // number that only moves on poll looks stuck.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!shouldShowCheck(run)) return null;

  const startedAt = run.started_at || run.created_at;
  const startedMs = startedAt ? new Date(startedAt).getTime() : NaN;
  // Elapsed counts from the start; stalled counts from the last sign of life.
  const ms = Number.isNaN(startedMs) ? 0 : now - startedMs;
  const stalled = isStalled(run, now);

  return (
    <div
      className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 mb-4"
      data-testid="check-in-progress"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3">
        <span className="w-2.5 h-2.5 rounded-full bg-sky-600 motion-safe:animate-pulse shrink-0" aria-hidden="true" />
        <span className="text-[13px] font-bold text-sky-800">
          {outcomeName ? `Checking ${outcomeName}` : 'Checking'}
        </span>
        <span className="ml-auto text-[12px] font-semibold text-sky-800 tabular-nums">
          {elapsedLabel(ms)} elapsed
        </span>
      </div>
      {/* Indeterminate: the harness reports the stage it is in, never a
          percentage, and a fake percentage on a commercial verification is
          exactly the kind of thing this product exists to stop. */}
      <div className="mt-2.5 h-[5px] rounded-full bg-sky-100 overflow-hidden" aria-hidden="true">
        <div className="h-full w-2/5 rounded-full bg-sky-600 tb-indeterminate" />
      </div>
      <div className="mt-2 flex items-center gap-3 text-[12.5px] text-gray-700">
        {/* The stage word alone. A sentence repeated verbatim under every
            state carries no information by construction, and "appears here"
            narrates the UI — both cut by the copy bar. */}
        <span>{stalled ? 'This is taking longer than usual.' : stageWordFor(run.status)}</span>
        {stalled && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="text-[12px] font-semibold px-2.5 py-1 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {retrying ? 'Starting…' : 'Retry the check'}
          </button>
        )}
        {retryError && <span className="text-coral-700 font-semibold">{retryError}</span>}
      </div>
    </div>
  );
}
